import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { motion } from 'framer-motion';
import { getCourse } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import FeaturedCourseLanding from './FeaturedCourseLanding';
import Skeleton from '../../components/ui/Skeleton';
import type { Course } from '../../types/course';

export default function CourseLandingPage(): React.JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const toast = useToast();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchCourse() {
      if (!slug) return;
      try {
        setLoading(true);
        const res = await getCourse(slug);
        if (!cancelled) {
          const courseData = res.course || res.data?.course;
          if (courseData) {
            setCourse(courseData as Course);
          } else {
            toast.show('Course not found', 'error');
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          toast.show(err.message || 'Failed to load course', 'error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchCourse();
    return () => { cancelled = true; };
  }, [slug, toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-6xl mx-auto px-4 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Skeleton className="h-8 w-32 rounded-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-24 w-full" />
              <div className="flex gap-3">
                <Skeleton className="h-12 w-40 rounded-xl" />
                <Skeleton className="h-12 w-40 rounded-xl" />
              </div>
            </div>
            <Skeleton className="h-80 w-full rounded-3xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <h1 className="text-2xl font-bold text-slate-900">Course Not Found</h1>
            <p className="text-slate-500">The course you are looking for does not exist or has been removed.</p>
          </motion.div>
        </div>
      </div>
    );
  }

  return <FeaturedCourseLanding course={course} />;
}
